// common-apis.js
// Purpose: Provides reusable API functions and hooks for common backend operations (e.g., knowledge base, workspace, authentication).
// Logic: Uses React hooks and context to access user state and navigation, and defines async functions for API calls used throughout the app.

/* eslint-disable eqeqeq */
import axios from "axios";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import { clog, getEncriptedLoginData } from "../lib/utils";
import { updateAppVersion } from "../lib/versionCheck";
const useCommonApi = () => {
  const navigate = useNavigate();
  const context = useContext(UserContext);
  const { userSpaces, setuserSpaces } = context;

  // Description: Creates a new knowledge base entry by sending a POST request to the backend API with the provided data. Handles authentication and error responses.
  const createKnowledgeBase = async (data) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/knowledgebase/create`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: data,
      });
      // clog(response, "response");
      if (!response.data.error) {
        return {
          error: false,
          data: response.data,
          message: response?.data?.message,
        };
      } else {
        return {
          error: true,
          message: response?.data?.message,
        };
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error);
      return {
        error: true,
        message: error?.response?.data?.message || error?.message,
      };
    }
  };
  // Description: Creates a knowledge base entry from a URL by sending a POST request to the backend API with the provided data. Handles authentication and error responses.
  const createKnowledgeBaseUrl = async (data) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/page/crawler`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: data,
      });
      // clog(response, "response");
      if (!response.data.error) {
        return {
          error: false,
          data: response.data,
          message: response?.data?.message,
        };
      } else {
        return {
          error: true,
          message: response?.data?.message,
        };
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error);
      return {
        error: true,
        message: error?.response?.data?.message || error?.message,
      };
    }
  };
  // Description: Fetches the list of members in a workspace by sending a POST request to the backend API with the workspace ID. Handles authentication and error responses.
  const getWorkSpaceMembers = async (workspace_id) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/workspace/members`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          workspace_id: workspace_id,
        },
      });
      if (!response.data.error) {
        return {
          error: false,
          message: response?.data?.message,
          members: response?.data,
        };
      } else {
        return {
          error: true,
          message: response?.data?.message,
        };
      }
    } catch (error) {
      clog(error);
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      return {
        error: true,
        message: error?.message,
      };
    }
  };
  // Description: Fetches the list of spaces in a workspace by sending a POST request to the backend API with the workspace ID. Handles authentication and error responses.
  const getSpaceList = async () => {
    try {
      // let workspaceId = JSON.parse(
      //   localStorage.getItem("selected_workspace")
      // )?.workspace_id;
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/workspace/space/list`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          workspace_id: JSON.parse(
            localStorage.getItem("selected_workspace") || "{}"
          )?.workspace_id,
        },
      });
      clog(response, "response");
      if (!response.data.error) {
        let userspaces = response.data.userspaces;
        userspaces = userspaces?.map((item, index) => {
          return {
            ...item,
            type: "folder",
            level: "1",
            submenu: [],
            open: false,
            tree_id: new Date().getTime().toString() + index + 1,
          };
        });
        setuserSpaces(userspaces);
        // setTimeout(() => {
        //   const asideContainer = asideRef.current;

        //   // Scroll to the bottom
        //   asideContainer.scrollTop = asideContainer.scrollHeight;
        // }, 10);
      } else {
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }

      clog(error);
    }
  };
  // Description: Fetches user login data using a token by sending a GET request to the backend API. Handles authentication and error responses.
  // Note: This function is typically used for single sign-on (SSO) or token-based authentication flows.
  const getLoginWithToken = async (data) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/vison/login/${data}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      clog(response, "response");
      if (response.data.success) {
        localStorage.setItem(
          "login_data",
          JSON.stringify(getEncriptedLoginData(response.data.data))
        );
        localStorage.setItem("login_token", response.data.token);
        // Update app version after successful login
        updateAppVersion();
        let data = response.data.data;
        if (data?.workspacess?.length === 0) {
          localStorage.setItem("workspaces_list", JSON.stringify([]));
          localStorage.setItem("selected_workspace", JSON.stringify({}));
        } else {
          let workspace_id = data.resp_user.workspace_id;
          if (workspace_id) {
            let workspace = data.workspacess.find(
              (item) => item.id == workspace_id
            );
            if (workspace) {
              localStorage.setItem(
                "selected_workspace",
                JSON.stringify(workspace || {})
              );
            }
          }
        }
        window.location.href = "/script";
        // navigate("/script");
      } else {
        return null;
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error);
      return null;
    }
  };
  // Description: Fetches the details of a specific knowledge base entry by sending a POST request to the backend API with the knowledge ID. Handles authentication and error responses.
  const getKnowledgeBase = async (knowledge_id) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/knowledgebase_details`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          knowledge_id: knowledge_id,
        },
      });
      // clog(response, "response");
      if (!response.data.error) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error);
    }
  };
  // Description: Fetches the list of knowledge base entries for a specific workspace by sending a POST request to the backend API with the workspace ID. Handles authentication and error responses.
  const getKnowledgeBaseList = async (workspace_id) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/knowledgebase_list`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          workspace_id: workspace_id,
        },
      });
      // clog(response, "response");
      if (!response.data.error) {
        return response?.data;
      } else {
        return null;
      }
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      clog(error);
      return null;
    }
  };
  // Description: Calls the delete file API by sending a POST request with the file data. Handles authentication and error responses.
  const callDeleteFileApiNode = async (data) => {
    try {
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/delete/upload_file`,
        data: data,
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      clog(response, "response");
      if (!response.data.error) {
        return {
          error: false,
          data: response.data,
        };
      } else {
        return {
          error: true,
          data: response.data.message,
        };
      }
    } catch (error) {
      clog(error, "error");
    }
  };
  // Description: Uploads multiple files to the knowledge base by sending a POST request with the files and metadata. Handles authentication and error responses.
  const uploadIcon = async (files, data) => {
    try {
      let formdata = new FormData();
      for (let i = 0; i < files.length; i++) {
        formdata.append("vsn_file", files[i]);
      }
      formdata.append("workspace_id", data.workspace_id);
      formdata.append("knowledge_name", data.knowledge_name);
      formdata.append("knowledge_tags", data.knowledge_tags);
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/upload/multifiles/knowledgebase`,
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
          jwt_token: localStorage.getItem("login_token"),
        },
        timeout: 10000,
        data: formdata,
      });

      if (!response.data.error) {
        return {
          error: false,
          data: response.data,
          message: response?.data?.message,
        };
      } else {
        return {
          error: true,
          message: response?.data?.message,
        };
      }
    } catch (error) {
      clog(error);
      return {
        error: true,
        message: error?.response?.data?.message || error?.message,
      };
    }
  };
  // Description: Deletes a file from the knowledge base by sending a POST request with the file data. Handles authentication and error responses.
  const deleteFileApi = async (data) => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/knowledgebase_delete`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: data,
      });
      // clog(response, "response");
      if (!response.data.error) {
        return {
          error: false,
          message: response?.data?.message,
        };
      } else {
        return {
          error: true,
          message: response?.data?.message,
        };
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      return {
        error: true,
        message: error?.response?.data?.message || error?.message,
      };
    }
  };
  // Description: Logs out the user by sending a POST request to the backend API with the user's email and token. Clears local storage and navigates to the home page.
  const logoutApi = async () => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/vison/logout`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          email: JSON.parse(localStorage.getItem("login_data"))?.resp_user
            ?.email,
          token: localStorage.getItem("login_token"),
        },
      });
      // clog(response, "response");
      localStorage.removeItem("login_data");
      localStorage.removeItem("selected_workspace");
      localStorage.removeItem("login_token");
      navigate("/");
      if (response.data.success) {
      } else {
      }
    } catch (error) {
      clog(error);
      navigate("/");
    }
  };
  const getWorkspaces = async () => {
    try {
      // Get the token from local storage
      const token = localStorage.getItem("login_token"); // Replace with your actual key

      // Dynamic workspace_id based on selectedworkspace

      // Make a GraphQL request using Axios
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_SIGNIN_URL}/graphql`,
        {
          query: `
            query{
              getUserworkspaces{
                user_id,
                workspace_id,
                user_type_id,
                workspace{
                    workspace_id,
                    workspace_name,
                    uniq_id,
                    user_id,
                    workspace_avtar,
                    workspace_icon,
                },
                user{
                    user_id,
                    first_name,
                    last_name,
                    email,
                    plan_id
                },
                user_menus{
                    person_level_id,
                    company_level_id,
                    script,
                    user_activity,
                    email_outreach,
                    sales,
                    knowledge_base,
                    monthly_calendar,
                    ai_employee,
                    marketing,
                    social,
                    notification,
                    specific_page_tracking,
                    exclusion_filter
                }
              }
            }
          `,
        },
        {
          headers: {
            jwt_token: token,
          },
        }
      );

      localStorage.setItem(
        "workspaces_list",
        JSON.stringify(response?.data?.data?.getUserworkspaces)
      );

      let loginData = JSON.parse(localStorage.getItem("login_data") || "{}");

      loginData["workspacess"] = response?.data?.data?.getUserworkspaces;

      localStorage.setItem("login_data", JSON.stringify(loginData));
      // clog(response.data, "graph ql workspace list");
      return response?.data?.data?.getUserworkspaces;
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      console.error("Error fetching data:", error);
    }
  };
  // Description: Checks if the user has access to Twitter by sending a GET request to the backend API. Handles authentication and error responses.
  async function checkTwitterAccess(setTwitterProfile, setChecking) {
    try {
      setChecking && setChecking(true);
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_USER_URL}/social/user/twitter_access`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      // clog(response, "response");
      if (!response.data.error) {
        setTwitterProfile(response?.data?.data);
      } else {
        setTwitterProfile(null);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setTwitterProfile(null);
    } finally {
      setChecking && setChecking(false);
    }
  }
  // Description: Fetches the list of AI agents by sending a GET request to the backend API. Handles authentication and error responses.
  const getAgentsData = async () => {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/ai/employee/list`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        // setagentData(response?.data?.aiEmployee || []);
        let data = response?.data?.aiEmployee || [];
        return {
          error: false,
          data: data,
        };
      } else {
        clog("Error in fetching notifications");
        // setagentData([]);
        return {
          error: true,
          data: [],
        };
      }
    } catch (error) {
      clog("Error in fetching notifications", error);
      if (error?.response?.status == 401 || error?.response?.status == 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      return {
        error: true,
        data: [],
      };
    }
  };
  return {
    createKnowledgeBase,
    createKnowledgeBaseUrl,
    getKnowledgeBase,
    getKnowledgeBaseList,
    getLoginWithToken,
    callDeleteFileApiNode,
    uploadIcon,
    deleteFileApi,
    logoutApi,
    checkTwitterAccess,
    getWorkspaces,
    getWorkSpaceMembers,
    getSpaceList,
    getAgentsData,
  };
};
export default useCommonApi;
